import Axios, {
  AxiosRequestConfig,
  AxiosRequestHeaders,
  AxiosResponse,
  AxiosError,
} from "axios";
import {
  QueryClient,
  QueryObserverResult,
  UseBaseMutationResult,
  UseInfiniteQueryResult,
} from "react-query";
import userGlobalConfig from "../constants/globalConfig";
import { secureStorage } from "../utils/secureStorage";
import { transformToApi, transformFromApi } from "../utils/apiTransformer";
import { manualRefreshToken, performLogout } from "../utils/tokenRefresh";
// CSRF imports removed - CSRF protection is disabled
// import { getCsrfToken, clearCsrfToken } from "../utils/csrf";

export type QueryReturnType<T> = QueryObserverResult<T, never>;
export type InfiniteQueryReturnType<T> = UseInfiniteQueryResult<T, never>;
export type MutateReturnType<T, U, V> = UseBaseMutationResult<T, V, U, unknown>;

const apiBaseUrl = process.env.REACT_APP_BASE_URL;

// No fallback — fail loudly rather than build an axios client with an undefined
// baseURL. .env.* are generated from the shared master.env (gen-env.sh).
if (!apiBaseUrl || apiBaseUrl.trim() === '') {
  throw new Error('REACT_APP_BASE_URL is not set — build with a generated .env (gen-env.sh).');
}

// Validated here (this module loads app-wide) though it's consumed in the
// listing screens: a missing value renders `undefined/uploads/...` <img src>.
if (!process.env.REACT_APP_ASSETS_URL || process.env.REACT_APP_ASSETS_URL.trim() === '') {
  throw new Error('REACT_APP_ASSETS_URL is not set — build with a generated .env (gen-env.sh).');
}

export const client = Axios.create({
  baseURL: apiBaseUrl,
  timeout: 30000, // 30 second timeout
  withCredentials: false, // Set to true if using cookies
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor with secure token handling, CSRF protection, and field transformation
async function authRequestInterceptor(config: AxiosRequestConfig) {
  // Get token from secure storage
  const encodedToken =
    secureStorage.getToken(userGlobalConfig.TOKEN) ||
    secureStorage.getToken(userGlobalConfig.USER_TOKEN);
  
  if (encodedToken) {
    config.params = { ...config.params };
  }
  
  if (config.headers) {
    // Add Authorization header
    if (encodedToken) {
      config.headers = {
        ...config.headers,
        Authorization: `Bearer ${encodedToken}`,
        Accept: "application/json",
      };
    }
    
    // CSRF protection disabled - backend does not use CSRF tokens
    // If you need to re-enable CSRF protection in the future:
    // 1. Uncomment the code below
    // 2. Add CSRF middleware back to the backend routes
    // 3. Create a /csrf-token endpoint in the backend
    
    /*
    const stateChangingMethods = ['POST', 'PUT', 'PATCH', 'DELETE'];
    const method = config.method?.toUpperCase();
    
    if (method && stateChangingMethods.includes(method)) {
      const isLoginEndpoint = config.url?.includes('/adminLogin') || config.url?.includes('/login');
      const isCsrfTokenEndpoint = config.url?.includes('/csrf-token');
      
      if (!isLoginEndpoint && !isCsrfTokenEndpoint && encodedToken) {
        try {
          const csrfToken = await getCsrfToken();
          if (csrfToken) {
            config.headers['X-CSRF-Token'] = csrfToken;
          }
        } catch (error) {
          console.error('❌ Error getting CSRF token:', error);
        }
      }
    }
    */
  }
  
  // Transform request body from camelCase to snake_case
  if (config.data && typeof config.data === 'object') {
    // Don't transform FormData (for file uploads)
    if (!(config.data instanceof FormData)) {
      console.log('[API] Transforming request body to snake_case:', config.data);
      config.data = transformToApi(config.data);
      console.log('[API] Transformed request body:', config.data);
    } else {
      console.log('[API] Skipping FormData transformation');
    }
  }
  
  // Transform query params from camelCase to snake_case
  if (config.params && typeof config.params === 'object') {
    console.log('[API] Transforming query params to snake_case:', config.params);
    config.params = transformToApi(config.params);
    console.log('[API] Transformed query params:', config.params);
  }
  
  return config;
}

// Response success interceptor - Transform snake_case to camelCase
function responseSuccessInterceptor(response: AxiosResponse) {
  try {
    // Transform response data from snake_case to camelCase
    if (response.data && typeof response.data === 'object') {
      console.log('[API] Transforming response from snake_case:', response.data);
      response.data = transformFromApi(response.data);
      console.log('[API] Transformed response to camelCase:', response.data);
    }
    return response;
  } catch (error) {
    console.error('[API] Response transformation error:', error);
    return response; // Return original response if transformation fails
  }
}

// Track if we're currently refreshing to prevent multiple refresh attempts
let isRefreshingToken = false;
let failedRequestsQueue: Array<{
  resolve: (value?: any) => void;
  reject: (reason?: any) => void;
  config: AxiosRequestConfig;
}> = [];

/**
 * Process queued requests after token refresh
 */
function processQueue(error: any = null, token: string | null = null) {
  failedRequestsQueue.forEach((promise) => {
    if (error) {
      promise.reject(error);
    } else {
      // Update the request with new token
      if (token && promise.config.headers) {
        promise.config.headers.Authorization = `Bearer ${token}`;
      }
      promise.resolve(client.request(promise.config));
    }
  });
  failedRequestsQueue = [];
}

// Response interceptor for error handling with token refresh
async function responseErrorInterceptor(error: AxiosError) {
  const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };
  
  // Transform error response if available
  if (error.response?.data && typeof error.response.data === 'object') {
    error.response.data = transformFromApi(error.response.data);
  }

  // ── Normalise error shape ────────────────────────────────────────────────
  // The API now returns { success, status, error: { code, domain, message, requestId } }
  // Older endpoints return { status, message }
  // We normalise both so all callers can use error.userMessage, error.errorCode, error.requestId
  const apiData = (error.response?.data || {}) as Record<string, any>;
  const apiError = apiData.error as Record<string, any> | undefined;
  const userMessage: string =
    apiError?.message ??    // new shape
    apiData.message ??      // old shape
    'An unexpected error occurred';
  const errorCode: string | undefined = apiError?.code;
  const requestId: string | undefined = apiError?.requestId;

  // Attach to the error object so per-screen toast calls pick them up automatically
  (error as any).userMessage = userMessage;
  (error as any).errorCode   = errorCode;
  (error as any).requestId   = requestId;

  // Build a user-facing message that includes the Reference ID when available
  const displayMessage = requestId
    ? `${userMessage} (Reference ID: ${requestId})`
    : userMessage;

  // Expose displayMessage so screens can use it: error?.displayMessage || error?.response?.data?.message
  (error as any).displayMessage = displayMessage;
  // ────────────────────────────────────────────────────────────────────────

  if (error.response) {
    // Handle specific error codes
    switch (error.response.status) {
      case 401:
        // Unauthorized - attempt token refresh
        
        // Don't retry if this is already a retry or if it's the refresh endpoint itself
        if (originalRequest._retry || originalRequest.url?.includes('refreshToken')) {
          console.error('❌ Token refresh failed or already retried, logging out');
          performLogout();
          return Promise.reject(error);
        }
        
        // Mark this request as retried
        originalRequest._retry = true;
        
        if (isRefreshingToken) {
          // If already refreshing, queue this request
          console.log('⏳ Token refresh in progress, queueing request');
          return new Promise((resolve, reject) => {
            failedRequestsQueue.push({ resolve, reject, config: originalRequest });
          });
        }
        
        isRefreshingToken = true;
        
        try {
          // Attempt to refresh the token
          const newToken = await manualRefreshToken();
          
          if (newToken) {
            console.log('✅ Token refreshed, retrying failed requests');
            
            // Update the original request with new token
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
            }
            
            // Process queued requests
            processQueue(null, newToken);
            
            isRefreshingToken = false;
            
            // Retry the original request
            return client.request(originalRequest);
          } else {
            // Refresh failed, logout
            console.error('❌ Token refresh returned null, logging out');
            processQueue(new Error('Token refresh failed'), null);
            isRefreshingToken = false;
            performLogout();
            return Promise.reject(error);
          }
        } catch (refreshError) {
          console.error('❌ Token refresh error:', refreshError);
          processQueue(refreshError, null);
          isRefreshingToken = false;
          performLogout();
          return Promise.reject(error);
        }
        
      case 403:
        // Forbidden - access denied
        console.error('Access forbidden');
        // CSRF handling disabled
        break;
      case 429:
        console.error('Too many requests - rate limited');
        break;
      case 500:
        console.error('Server error');
        break;
    }
  } else if (error.request) {
    console.error('Network error - no response received');
  }
  
  return Promise.reject(error);
}

client.interceptors.request.use(authRequestInterceptor as never);
client.interceptors.response.use(
  responseSuccessInterceptor,
  responseErrorInterceptor
);

export const mutatePostFn = async <T, U>(
  key: string,
  data: T,
  params?: Record<string, unknown>,
  headers?: AxiosRequestHeaders
): Promise<U> => {
  const response = await client.post<T, AxiosResponse<U>>(key, data, {
    params,
    headers,
  });
  return response.data;
};

export const mutatePatchFn = async <T, U>(
  key: string,
  data: T,
  params?: Record<string, unknown>
): Promise<U> => {
  const response = await client.patch<T, AxiosResponse<U>>(key, data, {
    params,
  });
  return response.data;
};

export const mutatePutFn = async <T, U>(
  key: string,
  data: T,
  params?: Record<string, unknown>
): Promise<U> => {
  const response = await client.put<T, AxiosResponse<U>>(key, data, {
    params,
  });
  return response.data;
};

export const mutateDeleteFn = async <T, U>(
  key: string,
  data: T,
  params?: Record<string, unknown>
): Promise<U> => {
  const response = await client.delete<T, AxiosResponse<U>>(key, {
    data,
    params,
  });
  return response.data;
};

const formatQueryParams = (
  params: Record<string, string | number> = {},
  pageParam?: number
) => {
  if (params || pageParam) {
    let qs = Object.keys(params)
      .map((key) => {
        const val = params[key];
        return `${val === null || val === undefined ? "" : `${key}=${val}`}`;
      })
      .filter(Boolean)
      .join("&");
    if (
      pageParam !== null &&
      pageParam !== undefined &&
      typeof pageParam === "number"
    ) {
      qs = qs.replace(`page=${pageParam - 1}`, "").concat(`&page=${pageParam}`);
    }
    return qs;
  }
};

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: async ({ queryKey: [url, params], pageParam = null }: any) => {
        if (typeof url === "string") {
          const queryParams = formatQueryParams(params as never, pageParam as number | undefined);
          // Append a timestamp to prevent Express ETag-based 304 responses.
          // A unique URL means the browser never sends If-None-Match, so the
          // server always returns a fresh 200 with the full response body.
          const cacheBuster = `_t=${Date.now()}`;
          const fullQuery = queryParams ? `${queryParams}&${cacheBuster}` : cacheBuster;
          const { data } = await client.get(
            `${url.toLowerCase()}?${fullQuery}`
          );
          return data;
        }
        throw new Error("Invalid QueryKey");
      },
      // Prevent automatic refetching that can cause loading states to persist
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      // Retry failed requests with exponential backoff
      retry: 1,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      // Set stale time to prevent immediate refetches
      staleTime: 5000,
      // Cache time
      cacheTime: 300000, // 5 minutes
    },
    mutations: {
      // Prevent mutations from retrying automatically
      retry: false,
      // Add timeout for mutations
      onError: (error: any) => {
        console.error('Mutation error:', error);
      },
    },
  },
});
